import { fetchConfig } from '@theme/utilities';
import { CartUpdateEvent } from '@theme/events';

/**
 * Buttons of the cart drawer upsell (snippets/cart-upsell.liquid).
 *
 * The markup is rendered with the header section, so every change requests that section back and hands it to the
 * regular CartUpdateEvent flow: cart-items-component morphs both drawers and the cart icon updates its count.
 */
class CartUpsellComponent extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.#onClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const button = event.target instanceof Element ? event.target.closest('[data-cart-upsell-action]') : null;
    if (!(button instanceof HTMLButtonElement) || button.disabled) return;

    const row = button.closest('[data-cart-upsell-row]');
    if (!(row instanceof HTMLElement) || row.getAttribute('aria-busy') === 'true') return;

    const isAdd = button.dataset.cartUpsellAction === 'add';
    const quantity = isAdd ? 1 : Number(button.dataset.quantity);

    // Where focus should land once the row has been re-rendered
    let focusKey = button.dataset.focusKey || '';
    if (isAdd) focusKey = 'plus';
    else if (quantity === 0) focusKey = 'add';

    this.#update(row, {
      url: isAdd ? Theme.routes.cart_add_url : Theme.routes.cart_change_url,
      body: isAdd
        ? { items: [{ id: Number(button.dataset.variantId), quantity: 1 }] }
        : { id: button.dataset.lineKey, quantity },
      focusKey,
    });
  };

  /**
   * @param {HTMLElement} row
   * @param {{ url: string, body: Record<string, unknown>, focusKey: string }} request
   */
  async #update(row, { url, body, focusKey }) {
    const sectionIds = new Set();
    document.querySelectorAll('cart-items-component').forEach((component) => {
      if (component instanceof HTMLElement && component.dataset.sectionId) sectionIds.add(component.dataset.sectionId);
    });

    const errorElement = row.querySelector('.cart-upsell__error');
    row.setAttribute('aria-busy', 'true');
    if (errorElement instanceof HTMLElement) errorElement.hidden = true;

    try {
      const response = await fetch(
        url,
        fetchConfig('json', {
          body: JSON.stringify({
            ...body,
            sections: Array.from(sectionIds).join(','),
            sections_url: window.location.pathname,
          }),
        })
      );
      const data = await response.json();

      if (!response.ok || data.errors || data.status) {
        throw new Error(data.description || data.message || data.errors || 'Could not update the cart');
      }

      const sections = data.sections || {};
      const [firstSectionId] = sectionIds;
      const sectionHtml = firstSectionId ? sections[firstSectionId] : undefined;
      const countText = sectionHtml
        ? new DOMParser().parseFromString(sectionHtml, 'text/html').querySelector('[ref="cartItemCount"]')?.textContent
        : undefined;

      this.#restoreFocusAfterRender(row.dataset.cartUpsellRow, focusKey);

      row.dispatchEvent(
        new CartUpdateEvent({}, 'cart-upsell', {
          itemCount: countText ? parseInt(countText, 10) : 0,
          source: 'cart-upsell',
          sections,
        })
      );
    } catch (error) {
      row.removeAttribute('aria-busy');
      if (errorElement instanceof HTMLElement) {
        errorElement.textContent = error instanceof Error ? error.message : String(error);
        errorElement.hidden = false;
      }
    }
  }

  /**
   * The clicked control is usually replaced by the re-render (Add ⇄ quantity), so put focus back on its counterpart.
   * @param {string | undefined} variantId
   * @param {string} focusKey
   */
  #restoreFocusAfterRender(variantId, focusKey) {
    if (!variantId || !focusKey) return;

    const focus = () => {
      // Looked up per render (the morph can replace the dialog). The mobile menu has its own, invisible drawer instance
      // that `cart:open` opens too, so pick the one that is actually rendered.
      const dialog = Array.from(document.querySelectorAll('dialog.cart-drawer__dialog')).find(
        (element) => element.open && element.getClientRects().length > 0
      );
      if (!dialog) return;

      const target = dialog.querySelector(
        `[data-cart-upsell-row="${CSS.escape(variantId)}"] [data-focus-key="${focusKey}"]`
      );
      if (target instanceof HTMLElement && !target.matches(':disabled')) {
        target.focus();
      } else if (!dialog.contains(document.activeElement)) {
        // Row is gone (a suggestion became a cart line) or the control is disabled: keep focus in the drawer
        const fallback =
          dialog.querySelector('[data-cart-upsell-row] [data-focus-key]:not(:disabled)') ||
          dialog.querySelector('[ref="closeButton"]');
        if (fallback instanceof HTMLElement) fallback.focus();
      }
    };

    // Both drawer instances (desktop and mobile menu) re-render the same section, so the row can be replaced more
    // than once; re-apply focus after each render for a short while.
    const onRendered = () => requestAnimationFrame(focus);
    document.addEventListener('cart:updated-items', onRendered);
    setTimeout(() => document.removeEventListener('cart:updated-items', onRendered), 2000);
  }
}

if (!customElements.get('cart-upsell')) {
  customElements.define('cart-upsell', CartUpsellComponent);
}
